import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Link as StyledLink,
  Switch,
  Text,
  InputGroup,
  InputRightElement,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, useTranslation } from "next-i18next";
import getConfig from "next/config";
import { CheckCircle, Eye, EyeOff } from "react-feather";
import { useFormContext } from "react-hook-form";
import validator from "validator";

const { publicRuntimeConfig } = getConfig();

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmRules: boolean;
  noAccountDeclaration: boolean;
  marketingAgreement: boolean;
};

type PasswordValidation = {
  length: boolean;
  lowercase: boolean;
  uppercase: boolean;
  numbers: boolean;
  symbols: boolean;
  valid: boolean;
};

const UserForm = ({ page = false }: { page: boolean }): React.ReactElement => {
  const {
    formState: { errors },
    register,
    watch,
  } = useFormContext<Inputs>();
  const [showPassword, setShowPassword] = useState(false);
  const password = watch("password");
  const isStrongPassword = useMemo(() => {
    const passwordString = password ?? "";
    const emptyOptions = {
      minLength: 0,
      minLowercase: 0,
      minUppercase: 0,
      minNumbers: 0,
      minSymbols: 0,
    };

    const passwordValidation: PasswordValidation = {
      length: validator.isStrongPassword(passwordString, {
        ...emptyOptions,
        minLength: 8,
      }),
      lowercase: validator.isStrongPassword(passwordString, {
        ...emptyOptions,
        minLowercase: 1,
      }),
      uppercase: validator.isStrongPassword(passwordString, {
        ...emptyOptions,
        minUppercase: 1,
      }),
      numbers: validator.isStrongPassword(passwordString, {
        ...emptyOptions,
        minNumbers: 1,
      }),
      symbols: validator.isStrongPassword(passwordString, {
        ...emptyOptions,
        minSymbols: 1,
      }),
      valid: false,
    };

    passwordValidation.valid =
      passwordValidation.length &&
      passwordValidation.lowercase &&
      passwordValidation.uppercase &&
      passwordValidation.numbers &&
      passwordValidation.symbols;

    return passwordValidation;
  }, [password]);
  const { t } = useTranslation("common");

  const PasswordValidationIcon = useCallback(
    (props: { icon: keyof PasswordValidation }) => (
      <CheckCircle
        size="16"
        color={
          isStrongPassword[props.icon]
            ? "#91c714"
            : errors.password
            ? "#d32929"
            : "#718096"
        }
        data-cy={`password-validation-${props.icon}-icon`}
      />
    ),
    [isStrongPassword]
  );

  return (
    <>
      <Heading as="h3" size="lg">
        {t("users.fillUserData")}
      </Heading>
      <FormControl isInvalid={!!errors.email} isRequired mb="15px">
        {page && <FormLabel>{t("users.email")}</FormLabel>}
        <Input
          autoFocus
          size={page ? "md" : "lg"}
          placeholder={page ? "" : t("users.email")}
          type="email"
          autoComplete="username"
          data-cy="email-field"
          {...register("email", {
            required: t("users.fieldRequired") + "",
            validate: (value) =>
              validator.isEmail(value)
                ? true
                : t("users.emailValidationError") + "",
          })}
        />
        {errors.email && (
          <FormErrorMessage>{errors.email.message}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl isInvalid={!!errors.firstName} isRequired mb="15px">
        {page && <FormLabel>{t("users.firstName")}</FormLabel>}
        <Input
          size={page ? "md" : "lg"}
          placeholder={page ? "" : t("users.firstName")}
          type="text"
          autoComplete="firstname"
          data-cy="first-name-field"
          {...register("firstName", {
            required: true,
            validate: (value) =>
              /([\-\'a-zA-ZąĄćĆęĘłŁńŃóÓśŚżŻźŹ])+/.test(value),
          })}
        />
        {errors.firstName && (
          <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl isInvalid={!!errors.lastName} isRequired mb="15px">
        {page && <FormLabel>{t("users.lastName")}</FormLabel>}
        <Input
          size={page ? "md" : "lg"}
          placeholder={page ? "" : t("users.lastName")}
          type="text"
          autoComplete="firstname"
          data-cy="last-name-field"
          {...register("lastName", {
            required: true,
            validate: (value) =>
              /([\-\'a-zA-ZąĄćĆęĘłŁńŃóÓśŚżŻźŹ])+/.test(value),
          })}
        />
        {errors.lastName && (
          <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl isInvalid={!!errors.password} isRequired mb="15px">
        {page && <FormLabel>{t("users.password")}</FormLabel>}
        <InputGroup>
          <Input
            size={page ? "md" : "lg"}
            placeholder={page ? "" : t("users.password")}
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            data-cy="password-field"
            {...register("password", {
              required: t("users.fieldRequired") + "",
              validate: () =>
                isStrongPassword.valid
                  ? true
                  : t("users.enterCorrectPassword") + "",
            })}
          />
          <InputRightElement>
            <Tooltip
              label={showPassword ? t("hide") : t("show")}
              placement="top"
            >
              <Icon
                as={showPassword ? EyeOff : Eye}
                w={5}
                h={5}
                mt={3}
                mr={3}
                cursor="pointer"
                onClick={() => setShowPassword(!showPassword)}
                transition={"all .25s ease-in-out"}
                _hover={{
                  opacity: 0.7,
                }}
                color="GrayText"
              />
            </Tooltip>
          </InputRightElement>
        </InputGroup>
        {errors.password && (
          <FormErrorMessage>{errors.password.message}</FormErrorMessage>
        )}
        <Box my="15px">
          <Flex align="center">
            <PasswordValidationIcon icon="length" />
            <Text ml={2} color="text.500" fontSize="xs">
              {t("users.passwordRules.1")}
            </Text>
          </Flex>
          <Flex align="center">
            <PasswordValidationIcon icon="lowercase" />
            <Text ml={2} color="text.500" fontSize="xs">
              {t("users.passwordRules.2")}
            </Text>
          </Flex>
          <Flex align="center">
            <PasswordValidationIcon icon="uppercase" />
            <Text ml={2} color="text.500" fontSize="xs">
              {t("users.passwordRules.3")}
            </Text>
          </Flex>
          <Flex align="center">
            <PasswordValidationIcon icon="numbers" />
            <Text ml={2} color="text.500" fontSize="xs">
              {t("users.passwordRules.4")}
            </Text>
          </Flex>
          <Flex align="center">
            <PasswordValidationIcon icon="symbols" />
            <Text ml={2} color="text.500" fontSize="xs">
              {t("users.passwordRules.5")}
            </Text>
          </Flex>
        </Box>
      </FormControl>
      <FormControl isInvalid={!!errors.confirmRules} isRequired>
        <Flex>
          <Switch
            id="confirmRules"
            isInvalid={!!errors.confirmRules}
            data-cy="rules-field"
            {...register("confirmRules", {
              required: t("users.fieldRequired") + "",
            })}
          />
          <FormLabel pl={2} verticalAlign="middle" htmlFor="confirmRules">
            <Trans i18nKey="users.acceptTerms">
              Potwierdzam zapoznanie z
              <StyledLink
                isExternal
                href={`${publicRuntimeConfig.basePath}/regulamin.pdf`}
              >
                Regulaminem
              </StyledLink>
            </Trans>
          </FormLabel>
        </Flex>
        {errors.confirmRules && (
          <FormErrorMessage>{errors.confirmRules.message}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl mt={1}>
        <Flex>
          <Switch
            id="marketingAgreement"
            data-cy="marketing-agreement-field"
            {...register("marketingAgreement")}
          />
          <FormLabel
            pb="0"
            pl={2}
            verticalAlign="middle"
            htmlFor="marketingAgreement"
          >
            <Trans i18nKey="users.marketingAgreement">
              Chcę otrzymywać newsletter od Accred.eu. Więcej informacji w
              <StyledLink
                isExternal
                href={`${publicRuntimeConfig.basePath}/polityka_prywatnosci.pdf`}
              >
                Polityce Prywatności
              </StyledLink>
            </Trans>
          </FormLabel>
        </Flex>
      </FormControl>
      <FormControl isInvalid={!!errors.noAccountDeclaration} mt={1} isRequired>
        <Flex>
          <Switch
            id="noAccountDeclaration"
            data-cy="no-account-declaration-field"
            isInvalid={!!errors.noAccountDeclaration}
            {...register("noAccountDeclaration", {
              required: t("users.fieldRequired") + "",
            })}
          />
          <FormLabel
            pb="0"
            pl={2}
            verticalAlign="middle"
            htmlFor="noAccountDeclaration"
          >
            {t("users.noAccountDeclaration")}
          </FormLabel>
        </Flex>
        {errors.noAccountDeclaration && (
          <FormErrorMessage>
            {errors.noAccountDeclaration.message}
          </FormErrorMessage>
        )}
      </FormControl>
      <Box mt="1rem">
        <Text fontSize={"xs"}>
          <Trans i18nKey="users.personalDataInfo">
            Administratorem danych osobowych Użytkowników systemu Accred jest
            Accred Sp. z o.o. Pełna informacja o przetwarzaniu danych osobowych
            znajduje się w
            <StyledLink
              isExternal
              href={`${publicRuntimeConfig.basePath}/polityka_prywatnosci.pdf`}
            >
              Polityce Prywatności
            </StyledLink>
          </Trans>
        </Text>
      </Box>
      {page && (
        <Flex mt="2rem" justify="center">
          <Button
            w="full"
            type="submit"
            //        isLoading={isSubmitting}
          >
            {t("users.signup")}
          </Button>
        </Flex>
      )}
    </>
  );
};

export default UserForm;
